import css from './WalletTicketMobile.module.scss'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import { EffectCoverflow } from 'swiper/modules'
// import SwiperCore, { EffectCoverflow, Swiper as SwiperClass } from 'swiper/'
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react'

import DataTestId from 'meta/dataTestId'
// import { SwiperClass } from 'swiper/swiper-react'
import { Breakpoints } from 'meta/layout/breakpoints'
import { IWalletEntityType } from 'meta/pages/v4/wallet'
import { useAppDispatch } from 'store'
import { CheckoutActions } from 'store/pages/checkout'
import { EventActions } from 'store/pages/event'
import { Dates } from 'utils/dates'

import { Grid, GridColumn } from 'components/Layout/Grid'
import Modal from 'components/Layout/Modal'
import Img from 'components/Media/Img'
import AddExtra from 'components/Pages/Wallet/AddExtra'
import { useUrlCloudfront } from 'hooks/useEnv'
import { useOnUpdate } from 'hooks/useOnUpdate'
import { useWalletEventTicketCodes } from 'hooks/useWallet'

import TicketDataFetcher from '../../BulkDataFetcher/TicketDataFetcher/TicketDataFetcher'
import TicketCardContainer from './TicketCard/TicketCardContainer'
import TicketCardSkeleton from './TicketCardSkeleton'
import TicketsProgress from './TicketsProgress'
import { WalletTicketMobileProps } from './WalletTicketMobileContainer'

// Import Swiper styles
import 'swiper/css'
import 'swiper/css/bundle'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/scrollbar'

const WalletTicketMobile: React.FC<WalletTicketMobileProps> = (props) => {
  const { eventData, locationData, baseProps } = props
  const { walletEventData, isPastEvent, eventActive, onClick } = baseProps

  const { t } = useTranslation()

  const dispatch = useAppDispatch()

  const urlCloudfront = useUrlCloudfront()

  const [activeCard, setActiveCard] = useState<number>(0)
  const [showSwiper, setShowSwiper] = useState<boolean>(false)

  const swiperClassRef = useRef<SwiperClass>(null)

  const [showAddExtraBalloon, setShowAddExtraBalloon] = useState<boolean>(false)

  const [fetchBulkData, setFetchBulkData] = useState<boolean>(false)
  const eventTicketCodesData = useWalletEventTicketCodes({ eventIdOrCodeUrl: String(eventData.id) })

  const useLongCard = eventData.goodsServices?.items.length > 0

  const openCheckout = (codeUrl: string) => {
    setShowAddExtraBalloon(false)
    dispatch(CheckoutActions.init({ codeUrl }))
  }

  const openExtraGoodsDialog = () => {
    dispatch(EventActions.getGoods({ codeUrl: eventData.codeUrl }))
    setShowAddExtraBalloon(true)
  }

  const selected = eventActive === eventData.id
  const hidden = eventActive !== null && eventActive !== eventData.id
  const backgroundImage =
    eventData.assets.length > 0
      ? eventData.assets[0].url
      : `${urlCloudfront}/images/carousel/placeholders/square_cover_ticketsms_placeholder.svg`

  useOnUpdate(() => {
    setShowSwiper(selected)
  }, [eventActive])

  useOnUpdate(() => {
    if (!showSwiper) setActiveCard(0)
  }, [showSwiper])

  useEffect(() => {
    if (showSwiper && !fetchBulkData) {
      setFetchBulkData(true)
    }
  }, [showSwiper])

  const date = new Date(eventData.dateEvent.date_tz)

  return (
    <>
      <Grid
        className={classNames(css.walletTicket, {
          [css.selected]: selected,
          [css.hidden]: hidden,
          [css.isPastEvent]: isPastEvent,
        })}
        onClick={() => {
          if (!hidden) onClick(eventData.id)
        }}
        data-testid={`${DataTestId.wallet.ticketsEventMobile}-${eventData.id}`}
      >
        <GridColumn
          gridColumnStart={{ [Breakpoints.mobile]: 1, [Breakpoints.tablet]: 3 }}
          gridColumnEnd={{ [Breakpoints.mobile]: 5 }}
        >
          <img className={css.walletTicket__img} src={backgroundImage} />
        </GridColumn>

        <GridColumn
          className={css.walletTicket__details}
          gridColumnStart={{ [Breakpoints.mobile]: 5 }}
          gridColumnEnd={{ [Breakpoints.mobile]: 13 }}
        >
          {walletEventData.type !== IWalletEntityType.event_collection && (
            <div className={css.date}>{Dates.format(date, t('dates:common:square'))}</div>
          )}

          {walletEventData.type == IWalletEntityType.event_collection && (
            <div className={css.walletTicket__eventCollection}>
              <span className={css.icon}>
                <Img src="/icons/checkout-season-header-icon.svg" />
              </span>
              <span className={css.text}>{t('wallet:seasonEvent')}</span>
            </div>
          )}

          <h5 className={css.name}>{eventData.name}</h5>

          <div className={css.location_container}>
            <Img src="/icons/location_marker.svg" />
            <div className={css.location_text}>{`${locationData.name} / ${locationData.city}`}</div>
          </div>
        </GridColumn>

        <GridColumn
          className={css.walletTicket__tickets}
          gridColumnStart={{ [Breakpoints.mobile]: 13 }}
          gridColumnEnd={{ [Breakpoints.mobile]: 17 }}
        >
          <div className={classNames(css.walletTicket__ticketsImg, { [css.shown]: !selected })}>
            <Img src={`/wallet/tickets_${walletEventData.count >= 3 ? 3 : walletEventData.count}.svg`} />
            <h5 data-testid={DataTestId.wallet.ticketCount} className={css.count}>
              {walletEventData.count}
            </h5>
          </div>
          <div className={classNames(css.walletTicket__ticketsImg, { [css.shown]: selected })}>
            <Img src={`/wallet/tickets_back.svg`} />
          </div>
        </GridColumn>
      </Grid>

      {fetchBulkData && <TicketDataFetcher eventId={eventData.id} />}

      {showSwiper && (
        <>
          <Swiper
            effect={'coverflow'}
            grabCursor={true}
            centeredSlides={true}
            slidesPerView={'auto'}
            coverflowEffect={{
              rotate: 50,
              stretch: 10,
              depth: 300,
              modifier: 1,
              slideShadows: false,
            }}
            modules={[EffectCoverflow]}
            pagination={false}
            className={css.walletTicket__ticketCards}
            onSlideChange={(swiper) => setActiveCard(swiper.activeIndex)}
            onAfterInit={(swiper) => {
              //@ts-expect-error
              swiperClassRef.current = swiper
            }}
          >
            {eventTicketCodesData &&
              eventTicketCodesData.map((elem, key) => (
                <SwiperSlide key={key}>
                  <TicketCardContainer
                    setShowAddExtraBalloon={openExtraGoodsDialog}
                    cod={elem.cod}
                    eventData={eventData}
                    locationData={locationData}
                    walletEventData={walletEventData}
                    idx={key}
                    activeCard={activeCard}
                    useLongCard={useLongCard}
                  />
                </SwiperSlide>
              ))}

            {!eventTicketCodesData && (
              <>
                <SwiperSlide>
                  <TicketCardSkeleton useLongCard={useLongCard} />
                </SwiperSlide>
                {walletEventData.count > 1 && (
                  <SwiperSlide>
                    <TicketCardSkeleton useLongCard={useLongCard} />
                  </SwiperSlide>
                )}
              </>
            )}
          </Swiper>

          {eventTicketCodesData && eventTicketCodesData.length > 0 && (
            <TicketsProgress
              activeCard={activeCard}
              //@ts-expect-error
              setActiveCard={(cardNumber: number) => swiperClassRef.current.slideTo(cardNumber)}
              eventTicketCodesData={eventTicketCodesData}
            />
          )}

          {showAddExtraBalloon && (
            <Modal
              closeIconPath="/icons/close_icon_white_smallcross.svg"
              transparent
              show={true}
              fullWidth={true}
              handleClose={() => setShowAddExtraBalloon(false)}
            >
              <AddExtra openCheckout={openCheckout} />
            </Modal>
          )}
        </>
      )}
    </>
  )
}

export default WalletTicketMobile
